import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import {
  defaultAward,
  defaultCertification,
  defaultEducation,
  defaultExperience,
  defaultInterest,
  defaultInternship,
  defaultLanguage,
  defaultProfile,
  defaultPublication,
  defaultProject,
  defaultReference,
  defaultSkill,
  defaultVolunteer,
} from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
  Input,
} from "@reactive-resume/ui";
import JSZip from "jszip";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/client/hooks/use-toast";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

type FormValues = {
  file?: File;
};

type CsvRow = Record<string, string>;

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const getFileExtension = (filename: string) => filename.split(".").pop()?.toLowerCase() ?? "";

const getColumnIndex = (cellRef: string) => {
  const columnRef = cellRef.replace(/\d+/g, "").toUpperCase();
  let index = 0;

  for (let i = 0; i < columnRef.length; i += 1) {
    index = index * 26 + (columnRef.charCodeAt(i) - 64);
  }

  return Math.max(index - 1, 0);
};

const getCellText = (cell: Element, sharedStrings: string[]) => {
  const type = cell.getAttribute("t");
  const valueNode = cell.getElementsByTagName("v")[0];
  const inlineStringNode = cell.getElementsByTagName("is")[0];

  if (type === "inlineStr" && inlineStringNode) {
    return Array.from(inlineStringNode.getElementsByTagName("t"))
      .map((node) => node.textContent ?? "")
      .join("");
  }

  const raw = valueNode?.textContent ?? "";
  if (!raw) return "";

  if (type === "s") {
    const sharedIndex = Number.parseInt(raw, 10);
    return Number.isNaN(sharedIndex) ? "" : sharedStrings[sharedIndex] ?? "";
  }

  return raw;
};

const parseCsv = (content: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    const next = content[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(cell);
      cell = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    cell += char;
  }

  row.push(cell);
  rows.push(row);

  return rows;
};

const parseXlsx = async (file: File): Promise<string[][]> => {
  const parser = new DOMParser();
  const zip = await JSZip.loadAsync(await file.arrayBuffer());

  const workbookXml = await zip.file("xl/workbook.xml")?.async("string");
  const workbookRelsXml = await zip.file("xl/_rels/workbook.xml.rels")?.async("string");

  if (!workbookXml || !workbookRelsXml) {
    throw new Error("Excel file is missing workbook data.");
  }

  const workbookDoc = parser.parseFromString(workbookXml, "application/xml");
  const relsDoc = parser.parseFromString(workbookRelsXml, "application/xml");

  const firstSheet = workbookDoc.getElementsByTagName("sheet")[0];
  const relationshipId = firstSheet?.getAttribute("r:id");
  if (!relationshipId) {
    throw new Error("Excel file does not contain a worksheet.");
  }

  const relationship = Array.from(relsDoc.getElementsByTagName("Relationship")).find(
    (node) => node.getAttribute("Id") === relationshipId,
  );
  const target = relationship?.getAttribute("Target");
  if (!target) {
    throw new Error("Could not resolve the worksheet inside the Excel file.");
  }

  const worksheetPath = `xl/${target.replace(/^\/+/, "").replace(/^xl\//, "")}`;
  const worksheetXml = await zip.file(worksheetPath)?.async("string");
  if (!worksheetXml) {
    throw new Error("Could not read the first worksheet from the Excel file.");
  }

  const sharedStringsXml = await zip.file("xl/sharedStrings.xml")?.async("string");
  const sharedStrings = sharedStringsXml
    ? Array.from(
        parser.parseFromString(sharedStringsXml, "application/xml").getElementsByTagName("si"),
      ).map((item) =>
        Array.from(item.getElementsByTagName("t"))
          .map((node) => node.textContent ?? "")
          .join(""),
      )
    : [];

  const worksheetDoc = parser.parseFromString(worksheetXml, "application/xml");
  const rowNodes = Array.from(worksheetDoc.getElementsByTagName("row"));

  return rowNodes
    .map((rowNode) => {
      const cells = Array.from(rowNode.getElementsByTagName("c"));
      const row: string[] = [];

      cells.forEach((cell) => {
        const ref = cell.getAttribute("r") ?? "";
        const index = ref ? getColumnIndex(ref) : row.length;
        row[index] = getCellText(cell, sharedStrings).trim();
      });

      return row.map((value) => value ?? "");
    })
    .filter((row) => row.some((value) => value.length > 0));
};

const parseSpreadsheet = async (file: File): Promise<CsvRow[]> => {
  const extension = getFileExtension(file.name);

  if (extension === "csv") {
    const raw = await file.text();
    return mapRowsToObjects(parseCsv(raw.replace(/^\uFEFF/, "")));
  }

  if (extension === "xlsx") {
    return mapRowsToObjects(await parseXlsx(file));
  }

  if (extension === "xls") {
    throw new Error("Legacy .xls files are not supported yet. Please save the sheet as .xlsx or .csv.");
  }

  throw new Error("Unsupported file type. Please upload a .csv or .xlsx file.");
};

const mapRowsToObjects = (rows: string[][]): CsvRow[] => {
  if (rows.length === 0) return [];

  const headers = rows[0].map((header) => header.trim());
  const dataRows = rows.slice(1);

  return dataRows
    .map((cells) => {
      const result: CsvRow = {};

      headers.forEach((header, index) => {
        result[header] = (cells[index] ?? "").trim();
      });

      return result;
    })
    .filter((row) => Object.values(row).some((value) => value.length > 0));
};

const valueFromRow = (row: CsvRow, ...aliases: string[]) => {
  const normalizedAliases = aliases.map((alias) => normalizeKey(alias));
  const entries = Object.entries(row);

  for (const [key, value] of entries) {
    if (normalizedAliases.includes(normalizeKey(key)) && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const htmlFromText = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) return trimmed;

  return `<ul>${lines.map((line) => `<li>${line.replace(/^[-*•]\s*/, "")}</li>`).join("")}</ul>`;
};

const splitCsvList = (value: string) =>
  value
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

export const AiCsvImportDialog = () => {
  const { isOpen, close } = useDialog("ai-csv-import");
  const { toast } = useToast();
  const setValue = useResumeStore((state) => state.setValue);
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({ defaultValues: { file: undefined } });

  const onImport = async () => {
    const file = form.getValues("file");
    if (!file) {
      toast({ variant: "error", title: "Please choose a CSV or Excel file first." });
      return;
    }

    try {
      setLoading(true);
      const csvRows = await parseSpreadsheet(file);

      if (csvRows.length === 0) {
        throw new Error("The uploaded file has no data rows.");
      }

      const first = csvRows[0];

      const summaryText = [
        valueFromRow(first, "Achievements_Summary"),
        valueFromRow(first, "Custom_Section"),
        valueFromRow(first, "Experience_Summary"),
      ]
        .filter(Boolean)
        .join("\n");

      setValue("metadata.template", "mastersunion");
      setValue("basics.name", valueFromRow(first, "Name"));
      setValue("basics.email", valueFromRow(first, "Email"));
      setValue("basics.phone", valueFromRow(first, "Phone"));
      setValue("basics.url.href", valueFromRow(first, "LinkedIn", "Profile_Website"));
      setValue("basics.url.label", "LinkedIn");
      setValue("basics.portfolio.href", valueFromRow(first, "Portfolio"));
      setValue("basics.portfolio.label", "Portfolio");
      setValue("sections.summary.content", htmlFromText(summaryText));

      const educationItems = csvRows
        .filter((row) => valueFromRow(row, "Education"))
        .map((row) => ({
          ...defaultEducation,
          id: createId(),
          visible: true,
          institution: { label: valueFromRow(row, "Education"), href: "" },
          date: valueFromRow(row, "Education_Duration"),
          area: valueFromRow(row, "Education_Location"),
          studyType: valueFromRow(row, "Education_Summary").split("|")[0]?.trim() ?? "",
          summary: htmlFromText(valueFromRow(row, "Education_Summary")),
        }));

      const experienceItems = csvRows
        .filter((row) => valueFromRow(row, "Experience"))
        .map((row) => ({
          ...defaultExperience,
          id: createId(),
          visible: true,
          company: { label: valueFromRow(row, "Experience"), href: "" },
          position: valueFromRow(row, "Experience_Position"),
          date: valueFromRow(row, "Experience_Duration"),
          location: valueFromRow(row, "Experience_Location"),
          summary: htmlFromText(valueFromRow(row, "Experience_Summary")),
        }));

      const internshipItems = csvRows
        .filter((row) => valueFromRow(row, "Internship"))
        .map((row) => ({
          ...defaultInternship,
          id: createId(),
          visible: true,
          company: { label: valueFromRow(row, "Internship"), href: "" },
          companyDescription: valueFromRow(row, "Internship_Desc"),
          position: valueFromRow(row, "Internship_Position"),
          date: valueFromRow(row, "Internship_Duration"),
          location: valueFromRow(row, "Internship_Location"),
          summary: htmlFromText(valueFromRow(row, "Internship_Summary")),
        }));

      const projectItems = csvRows
        .filter((row) => valueFromRow(row, "Projects"))
        .map((row) => ({
          ...defaultProject,
          id: createId(),
          visible: true,
          name: valueFromRow(row, "Projects"),
          summary: htmlFromText(valueFromRow(row, "Projects_Summary")),
        }));

      const certificationItems = csvRows
        .filter((row) => valueFromRow(row, "Certifications"))
        .map((row) => ({
          ...defaultCertification,
          id: createId(),
          visible: true,
          name: valueFromRow(row, "Certifications"),
          summary: htmlFromText(valueFromRow(row, "Certifications_Summary")),
        }));

      const languageItems = csvRows
        .flatMap((row) => {
          const names = splitCsvList(valueFromRow(row, "Language_Name"));
          const desc = valueFromRow(row, "Language_Desc");
          return names.map((name) => ({
            ...defaultLanguage,
            id: createId(),
            visible: true,
            name,
            description: desc,
          }));
        });

      const skillItems = csvRows
        .filter((row) => valueFromRow(row, "Skills_Name") || valueFromRow(row, "Skills_Desc"))
        .map((row) => ({
          ...defaultSkill,
          id: createId(),
          visible: true,
          name: valueFromRow(row, "Skills_Name"),
          description: splitCsvList(valueFromRow(row, "Skills_Desc")).join(" | "),
        }));

      const profileItems = csvRows
        .filter((row) => valueFromRow(row, "Profile") || valueFromRow(row, "Profile_Website"))
        .map((row) => ({
          ...defaultProfile,
          id: createId(),
          visible: true,
          network: valueFromRow(row, "Profile") || "LinkedIn",
          username: valueFromRow(row, "Profile_UserName") || valueFromRow(row, "Name"),
          icon: valueFromRow(row, "Profile_Icon") || "linkedin",
          url: {
            label: valueFromRow(row, "Profile"),
            href: valueFromRow(row, "Profile_Website"),
          },
        }));

      const awardItems = csvRows
        .filter((row) => valueFromRow(row, "Awads"))
        .map((row) => ({
          ...defaultAward,
          id: createId(),
          visible: true,
          title: valueFromRow(row, "Awads"),
          awarder: valueFromRow(row, "Awarder"),
          date: valueFromRow(row, "Awards_Date"),
          url: { label: "", href: valueFromRow(row, "Awards_Website") },
          summary: htmlFromText(valueFromRow(row, "Awards_Summary")),
        }));

      const interestItems = csvRows
        .filter((row) => valueFromRow(row, "Interests_Name"))
        .map((row) => ({
          ...defaultInterest,
          id: createId(),
          visible: true,
          name: valueFromRow(row, "Interests_Name"),
          keywords: splitCsvList(valueFromRow(row, "Interests_Keywords")),
        }));

      const publicationItems = csvRows
        .filter((row) => valueFromRow(row, "Publications_Summary"))
        .map((row) => ({
          ...defaultPublication,
          id: createId(),
          visible: true,
          name: "Publication",
          summary: htmlFromText(valueFromRow(row, "Publications_Summary")),
        }));

      const volunteerItems = csvRows
        .filter((row) => valueFromRow(row, "Volunteering_Summary"))
        .map((row) => ({
          ...defaultVolunteer,
          id: createId(),
          visible: true,
          organization: "Volunteering",
          summary: htmlFromText(valueFromRow(row, "Volunteering_Summary")),
        }));

      const referenceItems = csvRows
        .filter((row) => valueFromRow(row, "References") || valueFromRow(row, "References_Desc"))
        .map((row) => ({
          ...defaultReference,
          id: createId(),
          visible: true,
          name: valueFromRow(row, "References") || "Reference",
          description: valueFromRow(row, "References_Desc"),
          summary: htmlFromText(valueFromRow(row, "References_Summary")),
          url: { label: "", href: valueFromRow(row, "References_Website") },
        }));

      setValue("sections.education.items", educationItems);
      setValue("sections.experience.items", experienceItems);
      setValue("sections.internship.items", internshipItems);
      setValue("sections.projects.items", projectItems);
      setValue("sections.certifications.items", certificationItems);
      setValue("sections.languages.items", languageItems);
      setValue("sections.skills.items", skillItems);
      setValue("sections.profiles.items", profileItems);
      setValue("sections.awards.items", awardItems);
      setValue("sections.interests.items", interestItems);
      setValue("sections.publications.items", publicationItems);
      setValue("sections.volunteer.items", volunteerItems);
      setValue("sections.references.items", referenceItems);

      toast({ variant: "success", title: "File imported." });
      close();
      form.reset({ file: undefined });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to import file.";
      toast({ variant: "error", title: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>AI CSV Resume Import</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file to auto-fill your resume.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4">
            <FormField
              name="file"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CSV or Excel File</FormLabel>
                  <FormControl className="cursor-pointer">
                    <Input
                      type="file"
                      accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(event) => field.onChange(event.target.files?.[0])}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={onImport} disabled={loading}>
            {loading ? "Importing..." : "Import File"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
